import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import "./Home.css";
import { Container, Button, Box, Typography, Paper } from "@mui/material";
import NavMenu from "./Menu";
import { Link } from "react-router-dom";
import api from "../utils/axiosConfig";
import Cookies from "universal-cookie";
import { LazyLoadImage } from "react-lazy-load-image-component";
import "react-lazy-load-image-component/src/effects/blur.css";
import useWindowDimensions from "../utils/windowcheck";
import { setDogs, setActiveDog } from "./Items";
import ParticlesBg from "particles-bg";

const Home = () => {
  const { height } = useWindowDimensions();
  const dispatch = useDispatch();
  const { dogs, activeDog } = useSelector((state) => state.dogs);
  const cookies = new Cookies();
  const [loader, setLoading] = useState(true);

  const baseURL = api.defaults.baseURL || "";

  // -------------------- FETCH PETS --------------------
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const resp = await api.get("/api/getpets");
        const data =
          Array.isArray(resp.data) && resp.data.length
            ? resp.data
            : typeof resp.data === "string"
            ? JSON.parse(resp.data)
            : [];

        dispatch(setDogs(data));

        if (data.length === 0) {
          dispatch(setActiveDog(null));
          return;
        }

        const savedDog = cookies.get("activepet");
        let chosenDog = savedDog
          ? data.find((dog) => dog.name === savedDog.name)
          : data[0];

        if (!chosenDog && data.length > 0) chosenDog = data[0];

        dispatch(setActiveDog(chosenDog));
        cookies.set(
          "activepet",
          { name: chosenDog?.name },
          { path: "/", expires: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000) }
        );
      } catch (err) {
        console.error("❌ Error fetching pets:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [dispatch]);

  // -------------------- HANDLE DOG SELECTION --------------------
  const HandleDogUpdate = (adog) => {
    dispatch(setActiveDog(adog));
    cookies.set(
      "activepet",
      { name: adog.name },
      { path: "/", expires: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000) }
    );
  };

  // -------------------- SAFE IMAGE URL --------------------
  const normalizeImageUrl = (img) => {
    if (!img || typeof img !== "string") return "/defaultdog.jpg";
    if (img.startsWith("http")) return img;
    return `${baseURL}${img.startsWith("/") ? img : `/${img}`}`;
  };

  // -------------------- RENDER --------------------
  return (
    <div>
      <ParticlesBg type="circle" margin="5px" bg={true} />
      <NavMenu />

      <Container
        className="home-container"
        maxWidth="md"
        sx={{
          textAlign: "center",
          mt: 6,
          color: "#fff",
          position: "relative",
          zIndex: 2,
        }}
      >
        <Typography
          variant="h3"
          align="center"
          sx={{
            fontWeight: "bold",
            mb: 3,
            textShadow: "0px 2px 6px rgba(0,0,0,0.4)",
          }}
        >
          Knight's Kennel
        </Typography>

        {/* -------------------- FEATURED IMAGE -------------------- */}
        {activeDog?.image && (
          <Link to="/collages">
            <Paper
              elevation={10}
              sx={{
                overflow: "hidden",
                borderRadius: "20px",
                backdropFilter: "blur(6px)",
                bgcolor: "rgba(255,255,255,0.05)",
                p: 2,
              }}
            >
              <LazyLoadImage
                alt={`Knight's Kennel ${activeDog.name}`}
                src={normalizeImageUrl(activeDog.image)}
                effect="blur"
                height={height / 2}
                style={{
                  borderRadius: "16px",
                  width: "100%",
                  objectFit: "cover",
                  boxShadow: "0 8px 30px rgba(0,0,0,0.3)",
                }}
                onError={(e) => (e.target.src = "/defaultdog.jpg")}
              />
            </Paper>
          </Link>
        )}

        {/* -------------------- DOG BUTTONS -------------------- */}
        <Box
          sx={{
            mt: 5,
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
            gap: 2,
          }}
        >
          {(Array.isArray(dogs) ? dogs : []).map((dog) => {
            const isActive = dog.name === activeDog?.name;
            const imageSrc = normalizeImageUrl(dog.image);

            return (
              <Button
                key={dog.name}
                onClick={() => HandleDogUpdate(dog)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  gap: 1.2,
                  borderRadius: "20px",
                  textTransform: "none",
                  px: 2.5,
                  py: 1.2,
                  fontWeight: "bold",
                  width: 240,
                  height: 80,
                  background: isActive
                    ? "linear-gradient(135deg, #4A148C, #7B1FA2)"
                    : "linear-gradient(135deg, #0D47A1, #1976D2)",
                  color: "#fff",
                  boxShadow: isActive
                    ? "0 0 20px rgba(255,255,255,0.6)"
                    : "0 4px 12px rgba(0,0,0,0.4)",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "scale(1.08)",
                    boxShadow: "0 6px 20px rgba(0,0,0,0.6)",
                  },
                }}
              >
                <img
                  src={imageSrc}
                  alt={dog.name}
                  style={{
                    width: 50,
                    height: 50,
                    borderRadius: "50%",
                    objectFit: "cover",
                    border: "2px solid rgba(255,255,255,0.9)",
                    boxShadow: isActive
                      ? "0 0 12px rgba(255,255,255,0.8)"
                      : "0 0 4px rgba(0,0,0,0.3)",
                  }}
                  onError={(e) => (e.target.src = "/defaultdog.jpg")}
                />
                <Box sx={{ textAlign: "left" }}>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: "bold", fontSize: "1.1rem" }}
                  >
                    {dog.name}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.9 }}>
                    {dog.age
                      ? `${dog.age} yrs • ${dog.coat || "Beautiful coat"}`
                      : dog.coat || "Sweet temperament"}
                  </Typography>
                </Box>
              </Button>
            );
          })}
        </Box>

        {/* -------------------- FOOTER -------------------- */}
        {activeDog && (
          <Typography variant="h6" sx={{ mt: 3 }}>
            🐶 Welcome! View images of{" "}
            <Link
              to="/collages"
              style={{ textDecoration: "none", color: "#FFD54F" }}
            >
              <strong>{activeDog.name}</strong>
            </Link>{" "}
            🐶
          </Typography>
        )}

        <Typography variant="h5" sx={{ mt: 3, color: "#f8f8f8" }}>
          Home Breeders of High Quality Pups in Rhode Island
        </Typography>
      </Container>
    </div>
  );
};

export default Home;
